import type { TrackSummary } from '@anytracker/core'
import { Link } from 'react-router-dom'

import { IconButton } from '../../components/Button.js'
import { Icon, ICONS, Swatch } from '../../components/Layout.js'
import { Sparkline } from '../../charts/Sparkline.js'
import { trackColour, trackFill } from '../../charts/colours.js'
import { formatDate } from '../../format/dates.js'
import { kindDescription, kindLabel } from './kind-label.js'
import styles from './TrackList.module.css'

/** What has been recorded against a track, in words. Never a bare zero. */
function History({ summary }: { readonly summary: TrackSummary }) {
  const { measurementCount, firstAt, lastAt } = summary
  if (measurementCount === 0) {
    return <span>Never recorded</span>
  }
  return (
    <>
      <span>
        {measurementCount === 1
          ? '1 measurement'
          : `${String(measurementCount)} measurements`}
      </span>
      {firstAt !== undefined && lastAt !== undefined && (
        <span>
          {formatDate(firstAt)} – {formatDate(lastAt)}
        </span>
      )}
    </>
  )
}

function Name({ summary }: { readonly summary: TrackSummary }) {
  const { track } = summary
  return (
    <span className={styles['name']}>
      {track.name}
      {track.unit !== undefined && <span className={styles['unit']}>{track.unit}</span>}
    </span>
  )
}

/**
 * One track in the list: its colour, what it records, what has been recorded,
 * and the way into its editor (the Meadow design's Tracks screen).
 *
 * The whole row is the link. Archiving, deleting and reordering are not on it:
 * they are the list's edit mode (`TrackListScreen`) and the editor's own
 * "Archive this track", because a row that is a link and also a cluster of
 * small buttons is a row where a thumb hits the wrong one.
 */
export function TrackRow({ summary }: { readonly summary: TrackSummary }) {
  const { track, recent } = summary
  return (
    <Link to={`/tracks/${track.id}`} className={styles['row']}>
      <Swatch colour={trackFill(track)} size={12} />
      <span className={styles['main']}>
        <Name summary={summary} />
        <span className={styles['meta']}>
          <span>{kindDescription(track)}</span>
          <History summary={summary} />
        </span>
      </span>
      {/* Nothing at all for a track never recorded — see Sparkline. */}
      <Sparkline values={recent} colour={trackColour(track)} id={track.id} />
      <span className={styles['kind']}>{kindLabel(track)}</span>
      <Icon path={ICONS.chevronRight} className={styles['chevron']} />
    </Link>
  )
}

/**
 * A track in edit mode: the same identity, with the controls that change the
 * list rather than the track.
 *
 * Up and down buttons rather than drag and drop: HTML5 dragging does not work
 * on touch at all, this is a phone-first app, and buttons are keyboard and
 * screen-reader operable without any extra work.
 */
export function TrackEditRow({
  summary,
  position,
  total,
  onMove,
  onArchive,
  onRemove,
}: {
  readonly summary: TrackSummary
  readonly position: number
  readonly total: number
  readonly onMove: (from: number, to: number) => void
  readonly onArchive: () => void
  readonly onRemove: () => void
}) {
  const { track } = summary
  return (
    <div className={styles['row']}>
      <span className={styles['moves']}>
        <IconButton
          label={`Move ${track.name} up`}
          disabled={position === 0}
          onClick={() => onMove(position, position - 1)}
        >
          <Icon path="M6 15l6-6 6 6" />
        </IconButton>
        <IconButton
          label={`Move ${track.name} down`}
          disabled={position === total - 1}
          onClick={() => onMove(position, position + 1)}
        >
          <Icon path="M6 9l6 6 6-6" />
        </IconButton>
      </span>
      <Swatch colour={trackFill(track)} size={12} />
      <span className={styles['main']}>
        <Name summary={summary} />
        <span className={styles['meta']}>
          <History summary={summary} />
        </span>
      </span>
      <IconButton label={`Archive ${track.name}`} onClick={onArchive}>
        <Icon path="M4 7h16v13H4zM4 7l2-3h12l2 3M10 11h4" />
      </IconButton>
      <IconButton label={`Remove ${track.name}`} onClick={onRemove}>
        <Icon path="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />
      </IconButton>
    </div>
  )
}

/** An archived track: kept with its history, and one tap from coming back. */
export function ArchivedTrackRow({
  summary,
  onRestore,
  onRemove,
}: {
  readonly summary: TrackSummary
  readonly onRestore: () => void
  readonly onRemove: () => void
}) {
  const { track } = summary
  return (
    <div className={styles['row']}>
      <Swatch colour={trackFill(track)} size={12} />
      <span className={styles['main']}>
        <Name summary={summary} />
        <span className={styles['meta']}>
          <span>{kindDescription(track)}</span>
          <History summary={summary} />
        </span>
      </span>
      <IconButton label={`Restore ${track.name}`} onClick={onRestore}>
        <Icon path="M5 12a7 7 0 1 1 2 5M5 7v5h5" />
      </IconButton>
      <IconButton label={`Remove ${track.name}`} onClick={onRemove}>
        <Icon path="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />
      </IconButton>
    </div>
  )
}
